import React, { useImperativeHandle } from "react";
import { Input, Row, Checkbox, Modal, Form } from "antd";
import { isNonnegativeValidator } from "@/utils";
import { FormInstance } from "antd";
import InputNumber from "@/components/InputNumber";
import { EncryptedConsigneeInfoFormInstance, EncryptedConsigneeInfoFormValues } from "./type";

interface EncryptedConsigneeInfoFormProps {}

function getCodAmountsValidate(form: FormInstance<EncryptedConsigneeInfoFormValues>) {
    return {
        validator() {
            const { isCodCheckbox, codAmounts } = form.getFieldsValue();

            if (isCodCheckbox) {
                if (Number.isNaN(Number(codAmounts)) || Number(codAmounts) < 0) {
                    return Promise.reject("代收货款只能是大于0的数字");
                }
            }

            return Promise.resolve();
        },
    };
}

export default React.forwardRef<EncryptedConsigneeInfoFormInstance, EncryptedConsigneeInfoFormProps>((props, ref) => {
    const [formRef] = Form.useForm<EncryptedConsigneeInfoFormValues>();

    const getFormValues = async () => {
        try {
            return await formRef.validateFields();
        } catch (e) {
            Modal.error({
                title: "填写有误，请改正后再保存",
            });

            return Promise.reject(e);
        }
    };

    useImperativeHandle(ref, () => ({
        getFormValues,
        setFieldsValue: formRef.setFieldsValue,
    }));

    return (
        <>
            <div className="mb3" style={{ color: "#777777" }}>
                ① 收货人信息：
            </div>
            <div className="flex error ">
                <p>免责声明：</p>
                <p>
                    由于收货人信息十分重要。录入或导入完成后请检查收货人信息是否正确。
                    <br />
                    发货易软件不承担因收货人信息错误导致快递配送错误造成的损失
                </p>
            </div>

            <Form form={formRef} initialValues={{ shippingCost: 0, weight: 0 }}>
                <Form.Item name="provinceCode" noStyle hidden>
                    <Input size="small" disabled />
                </Form.Item>

                <Form.Item name="cityCode" noStyle hidden>
                    <Input size="small" disabled />
                </Form.Item>

                <Form.Item name="districtCode" noStyle hidden>
                    <Input size="small" disabled />
                </Form.Item>

                <Form.Item name="sourceOrderInfo" noStyle hidden>
                    <Input size="small" disabled />
                </Form.Item>

                <Row>
                    <Form.Item
                        label="姓名"
                        name="consigneeName"
                        className="fs12"
                        required={false}
                        rules={[
                            {
                                required: true,
                                message: "收货人姓名不能为空",
                            },
                        ]}
                    >
                        <Input size="small" style={{ width: 120 }} disabled></Input>
                    </Form.Item>
                    <Form.Item
                        label="手机"
                        name="mobile"
                        rules={[{ required: true }]}
                        className="ml10"
                        required={false}
                    >
                        <Input style={{ width: 120 }} size="small" disabled />
                    </Form.Item>
                    <Form.Item label="固话" name="telephone" style={{ margin: 0, marginLeft: 10 }}>
                        <Input style={{ width: 120 }} size="small" disabled />
                    </Form.Item>
                </Row>
                <Row>
                    <Form.Item label="省份" name="province">
                        <Input style={{ width: 120 }} size="small" disabled />
                    </Form.Item>

                    <Form.Item label="城市" name="city" className="ml10">
                        <Input style={{ width: 120 }} size="small" disabled />
                    </Form.Item>

                    <Form.Item label="区/县" name="district" className="ml10">
                        <Input style={{ width: 120 }} size="small" disabled />
                    </Form.Item>
                </Row>
                <Row>
                    <Form.Item label="街道" name="street">
                        <Input.TextArea rows={3} style={{ width: 480 }} size="small" disabled />
                    </Form.Item>
                </Row>
                <Row>
                    <Form.Item label="备注" name="sellerRemarks" style={{ margin: 0, marginBottom: 10 }}>
                        <Input.TextArea rows={3} placeholder="卖家备注" style={{ width: 480 }} size="small" />
                    </Form.Item>
                </Row>
                <Row>
                    <Form.Item label="留言" name="buyerRemarks">
                        <Input.TextArea rows={3} placeholder="买家留言" style={{ width: 480 }} size="small" disabled />
                    </Form.Item>
                </Row>
                <Row>
                    <Form.Item label="昵称" name="buyerNickname">
                        <Input style={{ width: 120 }} size="small" disabled />
                    </Form.Item>

                    <Form.Item name="buyerUsername" hidden>
                        <Input disabled />
                    </Form.Item>
                </Row>
                <Row>
                    <Form.Item label="折扣" name="discounts">
                        <InputNumber precision={2} style={{ width: 120 }} size="small" min={0} />
                    </Form.Item>
                    <Form.Item
                        label="运费"
                        name="shippingCost"
                        rules={[
                            {
                                validator: isNonnegativeValidator,
                            },
                        ]}
                        style={{ margin: 0, marginLeft: 10 }}
                    >
                        <InputNumber style={{ width: 120 }} suffix="元" min={0} size="small" />
                    </Form.Item>
                    <Form.Item
                        label="重量"
                        name="weight"
                        rules={[
                            {
                                validator: isNonnegativeValidator,
                            },
                        ]}
                        style={{ margin: 0, marginLeft: 10 }}
                    >
                        <InputNumber style={{ width: 120 }} suffix="kg" min={0} size="small" />
                    </Form.Item>
                </Row>
                <Row>
                    <Form.Item name="isCodCheckbox" valuePropName="checked" style={{ margin: 0, marginLeft: 40 }}>
                        <Checkbox>代收货款订单</Checkbox>
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(
                            prev: EncryptedConsigneeInfoFormValues,
                            next: EncryptedConsigneeInfoFormValues,
                        ) => prev.isCodCheckbox !== next.isCodCheckbox}
                    >
                        {(form: FormInstance<EncryptedConsigneeInfoFormValues>) => {
                            const { isCodCheckbox } = form.getFieldsValue();

                            if (isCodCheckbox) {
                                return (
                                    <>
                                        <Form.Item
                                            name="codAmounts"
                                            rules={[getCodAmountsValidate]}
                                            style={{ margin: 0 }}
                                        >
                                            <Input suffix="元" style={{ width: 150 }} size="small" />
                                        </Form.Item>

                                        <span className="error small ml5" style={{ lineHeight: "32px" }}>
                                            订单金额优先使用代收货款
                                        </span>
                                    </>
                                );
                            }

                            return null;
                        }}
                    </Form.Item>
                </Row>
            </Form>
        </>
    );
});
